// ./src/utils/api/CarHandoverService.ts
import { gql } from '@apollo/client';
import { ApiService } from './ApiService';
import { OpResult } from '@sdflc/api-helpers';
import { Car } from './CarService';

// ===========================================================================
// Fragments
// ===========================================================================

const HANDOVER_RECORD_FRAGMENT = gql`
  fragment HandoverRecordFields on HandoverRecord {
    id
    whenDone
    odometer
    categoryCode
    categoryName
    kindCode
    kindName
    whereDone
    shortNote
    comments
  }
`;

// ===========================================================================
// Query
// ===========================================================================

const REPORT_CAR_HANDOVER_GET_QUERY = gql`
  ${HANDOVER_RECORD_FRAGMENT}
  query ReportCarHandoverGet($params: HandoverReportParamsInput) {
    reportCarHandoverGet(params: $params) {
      code
      data {
        carId
        car {
          id
          label
          vin
          model
          color
          engineVolume
          manufacturedIn
          mileageIn
          initialMileage
          typeOfFuel
          tankVolume
          additionalTankVolume
          whenBought
          boughtFor
          boughtForCurrency
          boughtFrom
          whenSold
          soldFor
          soldForCurrency
          soldTo
          comments
          bodyTypeId
          transmissionTypeId
          engineTypeId
          makeId
          firstRecordId
          ownerNumber
          entityAttachmentId
          uploadedFileId
          driveType
          licensePlate
          mainTankFuelType
          mainTankVolume
          mainTankVolumeEnteredIn
          mainTankDefaultGrade
          addlTankFuelType
          addlTankVolume
          addlTankVolumeEnteredIn
          addlTankDefaultGrade
          vehicleMake {
            makeName
          }
          carBodyType {
            id
            name
          }
          carTransmissionType {
            name
          }
          uploadedFile {
            id
            fileUrl
            thumbnailUrl
          }
        }
        latestMileage
        generatedAt
        totalRecords
        records {
          ...HandoverRecordFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ===========================================================================
// Types
// ===========================================================================

/**
 * A single maintenance or repair record in the handover report.
 * Odometer is in the car's native mileage unit (car.mileageIn).
 * No pricing fields — this report is intended for the buyer.
 */
export interface HandoverRecord {
  id: string;
  whenDone: string;
  odometer?: number | undefined;
  categoryCode: string;
  categoryName: string;
  kindCode: string;
  kindName: string;
  whereDone?: string | undefined;
  shortNote?: string | undefined;
  comments?: string | undefined;
}

/**
 * Full handover report for a single vehicle.
 * Use car.mileageIn as the distance unit for latestMileage
 * and all record.odometer values.
 */
export interface HandoverReport {
  carId: string;
  /** Full car object — use car.mileageIn for all odometer display */
  car?: Car | undefined;
  /** Latest known odometer in car.mileageIn unit. Null if no records. */
  latestMileage?: number | undefined;
  generatedAt: string;
  totalRecords: number;
  records: HandoverRecord[];
}

export interface HandoverReportParamsInput {
  carId: string;
  timezoneOffset?: number | undefined;
}

// ===========================================================================
// Service
// ===========================================================================

export class CarHandoverService {
  static async get(params: HandoverReportParamsInput): Promise<OpResult> {
    return ApiService.executeQuery(REPORT_CAR_HANDOVER_GET_QUERY, { params });
  }
}